import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel, HttpTransportType } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../auth/auth.service';

export interface TransactionStatusChangeNotification {
  transactionId: number;
  newStatus: number;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: HubConnection | null = null;
  private isConnected = false;
  private isConnecting = false;
  private readonly connectionStatusSubject = new BehaviorSubject<boolean>(false);
  private readonly transactionStatusChangeSubject = new BehaviorSubject<TransactionStatusChangeNotification | null>(null);

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public transactionStatusChange$ = this.transactionStatusChangeSubject.asObservable();

  constructor(private readonly authService: AuthService) {}

  async startConnection(): Promise<void> {
    // If already connected or currently connecting, return early
    if ((this.hubConnection && this.isConnected) || this.isConnecting) {
      return;
    }

    // If there's an existing connection that's not connected, clean it up first
    if (this.hubConnection && !this.isConnected) {
      this.forceStopConnection();
    }

    this.isConnecting = true;

    try {
      const token = this.authService.getToken();
      if (!token) {
        console.warn('No authentication token available for SignalR connection');
        this.isConnecting = false;
        return;
      }

      const hubUrl = environment.hubURL + '/transaction';

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token,
          transport: HttpTransportType.LongPolling
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect([]) // Disable automatic reconnection
        .build();

      this.setupEventHandlers();

      await this.hubConnection.start();
      this.isConnected = true;
      this.isConnecting = false;
      this.connectionStatusSubject.next(true);
    } catch (error) {
      console.error('Error establishing SignalR connection:', error);
      this.isConnected = false;
      this.isConnecting = false;
      this.connectionStatusSubject.next(false);
      
      // Clean up failed connection
      this.hubConnection = null;
    }
  }


  private setupEventHandlers(): void {
    if (!this.hubConnection) {
      return;
    }

    this.hubConnection.on('TransactionStatusChanged', (notification: TransactionStatusChangeNotification) => {
      this.transactionStatusChangeSubject.next(notification);
    });

    this.hubConnection.onclose((error: unknown) => {
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnecting(() => {
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    });

    this.hubConnection.onreconnected(() => {
      this.isConnected = true;
      this.connectionStatusSubject.next(true);
    });
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  forceStopConnection(): void {
    if (this.hubConnection) {
      // Immediately reset state to prevent further operations
      this.hubConnection = null;
      this.isConnected = false;
      this.isConnecting = false;
      this.connectionStatusSubject.next(false);
    }
  }
}